/**
 * Veneers card body — species badge + stock preview + code
 * chips (with full prefix like `LH-EP101`) OR a compact rate row for
 * single-code veneers, plus the "Select Code & Add" / "Add to
 * Quotation" call-to-action.
 *
 * Extracted from ProductsPage.js. Pure JSX.
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus } from 'lucide-react';
import { formatINR } from '../../../lib/store';


function VeneerCodeChips({ group }) {
  const codes = group.veneer_codes.slice(0, 6);
  const remaining = group.veneer_codes.length - 6;
  const rates8x4 = group.veneer_codes.filter(c => c.rate_8x4 > 0).map(c => c.rate_8x4);
  const minRate = rates8x4.length ? Math.min(...rates8x4) : 0;
  const maxRate = rates8x4.length ? Math.max(...rates8x4) : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {codes.map((vc, vcIdx) => {
          // Build full code with prefix: LH-EP101
          const fullCode = group.code_prefix && vc.code
            ? `${group.code_prefix}-${vc.code}`
            : vc.code;
          return (
            <span
              key={vcIdx}
              className={`px-1.5 py-0.5 text-[10px] border rounded font-mono ${
                vc.stock_sheets > 0
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-heda-cream/60 border-heda-line text-heda-mute'
              }`}
            >
              {fullCode}
            </span>
          );
        })}
        {remaining > 0 && (
          <span className="px-1 py-0.5 text-[10px] text-heda-mute">+{remaining}</span>
        )}
      </div>
      {rates8x4.length > 0 && (
        <p className="text-xs">
          <span className="text-heda-sub">8x4:</span>
          <span className="font-mono-data font-semibold text-heda-olive-dark ml-1">
            {minRate === maxRate ? formatINR(minRate) : `${formatINR(minRate)}-${formatINR(maxRate)}`}/sqft
          </span>
        </p>
      )}
    </div>
  );
}


function VeneerSingleCodeRates({ group }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
      {group.rate_8x4 > 0 && (
        <span>
          <span className="text-heda-sub">8x4:</span>
          <span className="font-mono-data font-semibold text-heda-olive-dark ml-1">{formatINR(group.rate_8x4)}/sqft</span>
        </span>
      )}
      {group.rate_10x4 > 0 && (
        <span>
          <span className="text-heda-sub">10x4:</span>
          <span className="font-mono-data font-semibold text-heda-olive-dark ml-1">{formatINR(group.rate_10x4)}/sqft</span>
        </span>
      )}
    </div>
  );
}


export function VeneerBlock({ group, idx, onQuickAdd }) {
  const hasMultipleCodes = group.veneer_codes && group.veneer_codes.length > 1;
  return (
    <div className="space-y-2 flex-1 flex flex-col">
      {/* Top row: Veneer species + Stock */}
      <div className="flex items-center justify-between gap-2">
        {group.veneer_type && (
          <span className="px-2 py-0.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700">
            {group.veneer_type}
          </span>
        )}
        {group.stock_sheets > 0 && (
          <span className="text-xs text-emerald-700 font-medium">
            {group.stock_sheets} sheets
          </span>
        )}
      </div>

      {/* Codes+Rates block — multi-code chips OR single-code compact rates */}
      {hasMultipleCodes ? <VeneerCodeChips group={group} /> : <VeneerSingleCodeRates group={group} />}

      {/* Add Button */}
      <div className="mt-auto pt-2">
        <Button
          size="sm"
          className="rounded-xl w-full h-8 bg-heda-ink hover:bg-black text-white text-xs"
          onClick={() => onQuickAdd(group)}
          data-testid={`veneer-add-btn-${idx}`}
        >
          <Plus className="w-3 h-3 mr-1" />{' '}
          {hasMultipleCodes ? 'Select Code & Add' : 'Add to Quotation'}
        </Button>
      </div>
    </div>
  );
}
